import { usePackages, useProjectId } from '@consigli/hooks';
import { DocumentPackageType } from '@consigli/types';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Bid, MAX_COMPARISON_BIDS, OtherBidReference } from './types';

interface OtherBidsSelectorProps {
  bids: Bid[];
  selectedOtherBids: OtherBidReference[];
  onSelectedOtherBidsChange: (selectedOtherBids: OtherBidReference[]) => void;
}

export const OtherBidsSelector: FC<OtherBidsSelectorProps> = ({
  bids,
  selectedOtherBids,
  onSelectedOtherBidsChange,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { packages } = usePackages(projectId, DocumentPackageType.OFFER);

  const currentBidNames = useMemo(
    () =>
      new Set(
        bids.map((bid) => bid.providerName.trim().toLowerCase()).filter((name) => name.length > 0),
      ),
    [bids],
  );

  const otherBids: OtherBidReference[] = useMemo(() => {
    const collected: OtherBidReference[] = [];
    const seen = new Set<string>();
    for (const pkg of packages) {
      const rawBidsList = pkg.data?.bidslist;
      const bidsList = Array.isArray(rawBidsList) ? rawBidsList : [];
      for (const entry of bidsList) {
        const providerName = typeof entry === 'string' ? entry.trim() : '';
        if (!providerName) continue;
        if (currentBidNames.has(providerName.toLowerCase())) continue;
        const key = `${pkg.id}:${providerName.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        collected.push({ packageId: pkg.id, packageName: pkg.name, providerName });
      }
    }
    return collected;
  }, [packages, currentBidNames]);

  const isSelected = (bid: OtherBidReference) =>
    selectedOtherBids.some(
      (selected) =>
        selected.packageId === bid.packageId && selected.providerName === bid.providerName,
    );

  const toggleOtherBid = (bid: OtherBidReference) => {
    if (isSelected(bid)) {
      onSelectedOtherBidsChange(
        selectedOtherBids.filter(
          (selected) =>
            !(selected.packageId === bid.packageId && selected.providerName === bid.providerName),
        ),
      );
    } else if (selectedOtherBids.length < MAX_COMPARISON_BIDS) {
      onSelectedOtherBidsChange([...selectedOtherBids, bid]);
    }
  };

  const atLimit = selectedOtherBids.length >= MAX_COMPARISON_BIDS;

  return (
    <div className="w-full">
      <p className="font-semibold text-day-neutral-dark">
        {t('bid-review.compare.add-other-bids')}
      </p>
      <p className="text-sm text-day-neutral-subtle">
        {t('bid-review.compare.add-other-bids-description')}
      </p>
      {otherBids.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {otherBids.map((bid) => {
            const selected = isSelected(bid);
            const disabled = !selected && atLimit;
            return (
              <button
                key={`${bid.packageId}:${bid.providerName}`}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => toggleOtherBid(bid)}
                className={clsx(
                  'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                  selected
                    ? 'border-day-neutral-dark bg-day-neutral-dark text-day-light-5'
                    : disabled
                      ? 'cursor-not-allowed border-day-light-1 bg-day-light-5 text-day-neutral-subtle opacity-50'
                      : 'border-day-light-1 bg-day-light-5 text-day-neutral-dark hover:border-day-neutral-dark',
                )}
              >
                {bid.providerName}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 text-sm text-day-neutral-subtle">
          {t('bid-review.compare.no-other-bids')}
        </p>
      )}
      {atLimit && (
        <p className="mt-4 text-xs text-day-neutral-subtle">
          {t('bid-review.compare.max-other-bids-reached', { max: MAX_COMPARISON_BIDS })}
        </p>
      )}
    </div>
  );
};
